/**
 * CashRegisterMovementController — page
 * `/caisse-mouvement.html?id=<uuid>`
 *
 * EPIC-043 / 43.5 — Ajout d'un mouvement de caisse (entrée/sortie hors
 * vente, §7.J.C) :
 *   - charge le libellé de la session via GET /sessions/{id} pour
 *     l'afficher en sous-titre ;
 *   - validation côté client : `amount > 0`, `direction` ∈ {in, out},
 *     `reason` non vide ;
 *   - POST /v1/cash-register/sessions/{id}/movements ;
 *   - 201 → redirection vers la fiche session ;
 *   - 422 → erreurs sous les champs ;
 *   - 409 → banner critique « session déjà clôturée ».
 *
 * Convention `frontend/CLAUDE.md` : aucun HTML construit en JS.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

export default class CashRegisterMovementController extends BaseController {
  static targets = [
    "sessionCrumb",
    "sessionSubtitle",
    "errorBanner",
    "directionIn",
    "directionOut",
    "amountInput",
    "amountError",
    "reasonInput",
    "reasonError",
    "cancelLink",
    "submitBtn",
  ];

  connect() {
    this._submitting = false;
    this._sessionId = this._extractSessionId();
    if (!this._sessionId) {
      this._showErrorBanner("Session introuvable", "Identifiant de session manquant dans l'URL.");
      return;
    }
    this._renderStaticLinks();
    this._fetchSubtitle();
  }

  _extractSessionId() {
    const params = new URLSearchParams(window.location.search);
    return params.get("id") || null;
  }

  _renderStaticLinks() {
    const idEnc = encodeURIComponent(this._sessionId);
    if (this.hasSessionCrumbTarget) {
      this.sessionCrumbTarget.setAttribute("href", `/caisse-session.html?id=${idEnc}`);
    }
    if (this.hasCancelLinkTarget) {
      this.cancelLinkTarget.setAttribute("href", `/caisse-session.html?id=${idEnc}`);
    }
  }

  async _fetchSubtitle() {
    try {
      const response = await api.cashRegister.getSession(this._sessionId);
      if (!this.hasSessionSubtitleTarget) return;
      // Ressource enveloppée dans `{data: {...}}` côté backend.
      const session = response?.data ?? response;
      const dateLabel = this._formatDate(session.sessionDate);
      const label = session.label ? `${session.label} · ${dateLabel}` : dateLabel;
      this.sessionSubtitleTarget.textContent = `Session : ${label}`;
    } catch (error) {
      // Non bloquant : le sous-titre reste la valeur statique de la maquette.
    }
  }

  async submit(event) {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    if (this._submitting) return;
    this._clearFieldErrors();
    this._hideErrorBanner();

    const direction = this._readDirection();
    const amount = this.hasAmountInputTarget ? Number(this.amountInputTarget.value) : NaN;
    const reason = this.hasReasonInputTarget ? this.reasonInputTarget.value.trim() : "";

    let hasError = false;
    if (!Number.isFinite(amount) || amount <= 0) {
      this._showFieldError("amountError", "Le montant doit être supérieur à 0.");
      hasError = true;
    }
    if (reason === "") {
      this._showFieldError("reasonError", "Le motif est obligatoire.");
      hasError = true;
    }
    if (hasError) return;

    const payload = { direction, amount: amount.toFixed(2), reason };

    this._setSubmitting(true);
    try {
      await api.cashRegister.addMovement(this._sessionId, payload);
      const idEnc = encodeURIComponent(this._sessionId);
      window.location.assign(`/caisse-session.html?id=${idEnc}`);
    } catch (error) {
      this._setSubmitting(false);
      const status = error instanceof APIError ? error.status : 0;
      if (status === 409) {
        this._showErrorBanner(
          "Session déjà clôturée",
          "Vous ne pouvez plus ajouter de mouvement à cette session.",
        );
      } else if (status === 422) {
        this._applyFieldErrors(error);
      } else {
        const msg = (error instanceof APIError && error.message) || "Impossible d'enregistrer le mouvement.";
        this._showErrorBanner("Erreur", msg);
      }
    }
  }

  _readDirection() {
    if (this.hasDirectionInTarget && this.directionInTarget.checked) return "in";
    if (this.hasDirectionOutTarget && this.directionOutTarget.checked) return "out";
    return "out";
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _setSubmitting(submitting) {
    this._submitting = submitting;
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = submitting;
      this.submitBtnTarget.setAttribute("aria-busy", submitting ? "true" : "false");
    }
  }

  _showErrorBanner(title, message) {
    if (!this.hasErrorBannerTarget) {
      this.showError(`${title} — ${message}`);
      return;
    }
    const titleEl = this.errorBannerTarget.querySelector('[data-slot="error-title"]');
    const msgEl = this.errorBannerTarget.querySelector('[data-slot="error-message"]');
    if (titleEl) titleEl.textContent = title;
    if (msgEl) msgEl.textContent = " " + message;
    this.errorBannerTarget.hidden = false;
  }

  _hideErrorBanner() {
    if (this.hasErrorBannerTarget) this.errorBannerTarget.hidden = true;
  }

  _clearFieldErrors() {
    for (const name of ["amountError", "reasonError"]) {
      const has = "has" + name.charAt(0).toUpperCase() + name.slice(1) + "Target";
      if (this[has]) {
        const targetName = name + "Target";
        this[targetName].textContent = "";
        this[targetName].hidden = true;
      }
    }
  }

  _showFieldError(targetName, message) {
    const has = "has" + targetName.charAt(0).toUpperCase() + targetName.slice(1) + "Target";
    if (!this[has]) return;
    const el = this[targetName + "Target"];
    el.textContent = message;
    el.hidden = false;
  }

  _applyFieldErrors(error) {
    const details = error instanceof APIError ? error.details : null;
    const fields = details && Array.isArray(details.fields) ? details.fields : [];
    let matched = false;
    for (const f of fields) {
      const fieldName = (f.field || "").toString();
      if (fieldName === "amount") {
        this._showFieldError("amountError", f.message || "Montant invalide.");
        matched = true;
      } else if (fieldName === "reason") {
        this._showFieldError("reasonError", f.message || "Motif invalide.");
        matched = true;
      } else if (fieldName === "direction") {
        this._showErrorBanner("Erreur de saisie", f.message || "Direction invalide.");
        matched = true;
      }
    }
    if (!matched) {
      const msg = (error instanceof APIError && error.message) || "Données invalides.";
      this._showErrorBanner("Données invalides", msg);
    }
  }

  _formatDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  }
}
